import KeyboardOutlinedIcon from "@mui/icons-material/KeyboardOutlined";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
} from "@mui/material";
import React, { useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";

import { TextInput } from "components/common/TextInput";
import { Typography } from "new-components/typography/Typography";
import { unclearOptionsArr } from "utils/ReviewPageUtils";

export interface UnclearButtonProps {
  setUnclearMenuOpen: (state: boolean) => void;
  isUnclearMenuOpen: boolean;
  onSubmit: (feedbackText: string) => void;
  showButton: boolean;
  hideHotkey?: boolean;
}

export function UnclearButton(props: UnclearButtonProps) {
  const { setUnclearMenuOpen, isUnclearMenuOpen, showButton, onSubmit, hideHotkey } = props;

  const backgroundColor = "#FFFFFF";
  const colorText = "#919599";

  if (!showButton) return <Card className="rounded-2xl h-[3.5vh] w-[20vw] self-center bg-zinc-200" />;

  return (
    <>
      <Button
        id="unclear-button"
        data-testid="unclear-button"
        onClick={() => setUnclearMenuOpen(true)}
        variant="contained"
        sx={{
          height: "34px",
          flexShrink: 0,
          bgcolor: backgroundColor,
          borderRadius: "17px",
          textTransform: "none",
          justifyContent: "center",
          color: colorText,
          fontSize: "16px",
          whiteSpace: "nowrap",
          padding: "0px 14px",
          "& .MuiButton-endIcon": {
            margin: "0",
            marginLeft: "8px",
          },
        }}
        endIcon={
          !hideHotkey && (
            <Box id="unclear-button-end-icon" sx={{
              display: { xs: 'none', sm: 'none', md: 'flex' },
              height: '100%',
              width: '36px',
              alignItems: 'center',
              justifyContent: 'space-between',
              color: '#919599',
              opacity: 0.6,
            }}>
              <KeyboardOutlinedIcon sx={{ height: '24px', width: '24px' }} />
              <Typography variant="detail" fontWeight="bold">U</Typography>
            </Box>
          )
        }
      >
        Unclear?
      </Button>
      {isUnclearMenuOpen && (
        <UnclearModal open={isUnclearMenuOpen} onClose={() => setUnclearMenuOpen(false)} onSubmit={onSubmit} />
      )}
    </>
  );
}

export interface UnclearModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (feedbackText: string) => void;
}

export function UnclearModal(props: UnclearModalProps) {
  const { open, onClose, onSubmit } = props;

  const [unclearOptions, setUnclearOptions] = useState(new Set());
  const [textValue, setTextValue] = useState("");

  const handleUnclearOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, name: valueOption } = event.target;
    setUnclearOptions((prevOptions) => {
      const newOpts = new Set(prevOptions);
      if (checked) {
        newOpts.add(valueOption);
      } else {
        newOpts.delete(valueOption);
      }
      return newOpts;
    });
  };

  function isValidForm() {
    const optionHasValue = unclearOptions.size > 0;
    return optionHasValue || textValue.trim().length > 0;
  }

  const convertFeedbackOptions = () => {
    let result = "";

    if (unclearOptions.size > 0) {
      result = Array.from(unclearOptions).join("; ");
    }

    if (textValue) {
      result += result.length > 0 ? `; ${textValue}` : textValue;
    }
    return result;
  };

  const canSubmit = open && isValidForm();

  const handleSubmit = () => {
    if (!canSubmit) {
      return;
    }
    onClose();
    onSubmit(convertFeedbackOptions());
  };

  useHotkeys(
    "enter",
    (event) => {
      event.preventDefault();
      handleSubmit();
    },
    { enabled: canSubmit, enableOnFormTags: ["INPUT", "TEXTAREA"] },
    [canSubmit, unclearOptions, textValue, onClose, onSubmit]
  );

  useHotkeys(
    "esc",
    () => {
      onClose();
    },
    { enabled: open, enableOnFormTags: ["INPUT", "TEXTAREA"] },
    [open, onClose]
  );

  return (
    // Z-index is set to 1302 to be an step above banner of the COUNT popper in the Query Detail modal
    <Dialog open={open} onClose={onClose} scroll="paper" maxWidth="md" fullWidth sx={{ zIndex: 1302 }}>
      <DialogTitle id="scroll-dialog-title" className="flex justify-center items-center bg-[#0450BA] p-4">
        <Typography className="text-white">
          Reason Marked Unclear?
        </Typography>
      </DialogTitle>
      <DialogContent className="bg-zinc-100 max-w-full">
        <Box className="flex flex-col items-center">
          <Typography align="center" className="my-6">
            (check all that apply)
          </Typography>
          <Stack className="w-[90%]" spacing={2}>
            {unclearOptionsArr.map(({ name, value }) => (
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox checked={unclearOptions.has(value)} onChange={handleUnclearOptionChange} name={value} />
                  }
                  label={
                    <Typography className="ml-2 mb-1">
                      {name}
                    </Typography>
                  }
                />
                <Divider />
              </Box>
            ))}
          </Stack>
          <Box className="flex w-full justify-center my-6">
            <TextInput
              id="feedbackText"
              placeholder="Enter feedback here"
              className="w-[90%] bg-zinc-50"
              value={textValue}
              onChange={(e) => setTextValue(e.target.value)}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions className="flex justify-center gap-4 py-4 w-[90%] mx-auto">
        <Button className="bg-black text-xl text-white w-[35%]" variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button
          className="text-white text-xl bg-[#214FB3] w-[65%] h-full disabled:bg-neutral-400"
          variant="outlined"
          onClick={handleSubmit}
          disabled={!isValidForm()}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
